import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslocoModule, TRANSLOCO_SCOPE, TranslocoScope, TranslocoService } from '@jsverse/transloco';
import { filter, firstValueFrom, tap } from 'rxjs';
import {
  CustomMenuItem,
  DeightonHeaderComponent,
  DeightonNavigationItem,
  DeightonNavigationMenuComponent,
  findLeafByRoute,
  NavigationItems,
  ProductMenuItem
} from '@deighton/shared-ui/layout';
import { environment } from '../../../environments/environment';
import { LicensingAuthService } from '../../../services/licensing-auth.service';
import { RoleService } from '../../../services/role/role.service';
import { LicensingRole } from '../../../shared/constants/licensing-role.enum';

/** Main layout component for the Licensing application. */
@Component({
  selector: 'd-licensing-layout',
  imports: [DeightonHeaderComponent, DeightonNavigationMenuComponent, RouterOutlet, TranslocoModule],
  templateUrl: './app-layout.component.html',
  styleUrl: './app-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppLayoutComponent implements OnInit {
  /** Destroy reference for automatic observable cleanup. */
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /** Angular Router for navigation. */
  private readonly router: Router = inject(Router);

  /** Activated route for relative navigation (works in both standalone and shell-app). */
  private readonly route: ActivatedRoute = inject(ActivatedRoute);

  /** Auth service for standalone-mode logout. */
  private readonly authService: LicensingAuthService = inject(LicensingAuthService);

  /** Transloco translation provider. */
  private readonly translateService: TranslocoService = inject(TranslocoService);

  /** Transloco translations scope used for this component. */
  private readonly translocoScope: TranslocoScope = inject(TRANSLOCO_SCOPE);

  /** Role service for determining navigation visibility. */
  private readonly roleService: RoleService = inject(RoleService);

  /** Reference to the navigation menu component instance. */
  public menuElement: Signal<DeightonNavigationMenuComponent | undefined> = viewChild(
    DeightonNavigationMenuComponent
  );

  /** Whether the navigation menu is open. */
  public navigationMenuOpen: WritableSignal<boolean> = signal(false);

  /** List of items to show in the side navigation menu. */
  public navigationMenuItems: WritableSignal<Array<DeightonNavigationItem>> = signal(
    new Array<DeightonNavigationItem>()
  );

  /**
   * Tracks which navigation leaf item is currently active.
   * Driven by the router when the path matches the item route.
   */
  public activeNavItem: WritableSignal<string | null> = signal(null);

  /** Custom menu items for the header settings menu. */
  public hostAppCustomMenuItems: WritableSignal<Array<CustomMenuItem>> = signal(new Array<CustomMenuItem>());

  /** Product menu items displayed in the header. */
  public productMenuItems: WritableSignal<Array<ProductMenuItem>> = signal(new Array<ProductMenuItem>());

  /** Initializes the navigation menu options. */
  public async ngOnInit(): Promise<void> {
    // Close the navigation menu and update the active highlight whenever a route change completes
    this.router.events
      .pipe(
        filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
        tap((_event: NavigationEnd): void => {
          this.navigationMenuOpen.set(false);
        }),
        tap((event: NavigationEnd): void => {
          this.updateActiveItemFromUrl(event.urlAfterRedirects);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    await this.initializeMenuOptions();

    // Set active item for initial page load
    this.updateActiveItemFromUrl(this.router.url);
  }

  /** Handles the menu button click to toggle sidebar visibility. */
  public handleMenuBtnClicked(): void {
    const menu: DeightonNavigationMenuComponent | undefined = this.menuElement();
    if (menu) {
      menu.toggle();
    }
  }

  /** Handler for the logout click event. Signs out and redirects to the backend login page. */
  public handleLogout(): void {
    if (environment.okta) {
      this.authService.signOut();
      this.authService.redirectToLogin();
    }
  }

  /** Handler for the support button click event. */
  public onSupportClicked(): void {
    // TODO: Implement support click logic
  }

  /** Handler for the help button click event. */
  public onHelpClicked(): void {
    // TODO: Implement help click logic
  }

  /** Handler for the user settings click event. */
  public onUserSettingsClicked(): void {
    // TODO: Implement user settings logic
  }

  /** Handler for the system info click event. */
  public onSystemInfoClicked(): void {
    // TODO: Implement system info logic
  }

  /**
   * Initializes the navigation menu options with translations.
   * Uses relative navigation so routes work in both standalone and shell-app modes.
   */
  private async initializeMenuOptions(): Promise<void> {
    await this.roleService.initialize();
    await firstValueFrom(this.translateService.load(this.translateService.getActiveLang()));

    const translate: (key: string) => Promise<string> = (key: string): Promise<string> =>
      firstValueFrom(this.translateService.selectTranslate(key, {}, this.translocoScope));

    const [
      homeLabel,
      licensesLabel,
      individualLabel,
      organizationalLabel,
      plansLabel,
      rolesLabel
    ]: Array<string> = await Promise.all([
      translate('layout.navigation.home'),
      translate('layout.navigation.licenses'),
      translate('layout.navigation.individual'),
      translate('layout.navigation.organizational'),
      translate('layout.navigation.plans'),
      translate('layout.navigation.roles')
    ]);

    const licenseChildren: Array<NavigationItems> = [
      {
        label: individualLabel,
        icon: 'person',
        route: 'licensing/individual',
        onClickFunction: (): void => {
          void this.router.navigate(['licensing/individual'], { relativeTo: this.route });
        }
      }
    ];

    // Organisation and Plans are only visible to TenantAdmin and above
    const hasTenantAdminRole: boolean = this.roleService.hasRole(LicensingRole.TenantAdmin);
    if (hasTenantAdminRole) {
      licenseChildren.push(
        {
          label: organizationalLabel,
          icon: 'group',
          route: 'licensing/organization',
          onClickFunction: (): void => {
            void this.router.navigate(['licensing/organization'], { relativeTo: this.route });
          }
        },
        {
          label: plansLabel,
          icon: 'modelling',
          route: 'licensing/plans',
          onClickFunction: (): void => {
            void this.router.navigate(['licensing/plans'], { relativeTo: this.route });
          }
        }
      );
    }

    const menuItems: Array<DeightonNavigationItem> = [
      {
        label: homeLabel,
        icon: 'home',
        route: './',
        onClickFunction: (): void => {
          void this.router.navigate(['./'], { relativeTo: this.route });
        }
      },
      new DeightonNavigationItem({
        label: licensesLabel,
        icon: 'license',
        children: licenseChildren
      }),
      new DeightonNavigationItem({
        label: rolesLabel,
        icon: 'admin_panel_settings',
        route: 'roles/user',
        onClickFunction: (): void => {
          void this.router.navigate(['roles/user'], { relativeTo: this.route });
        }
      })
    ];

    this.navigationMenuItems.set(menuItems);
  }

  /**
   * Updates the active navigation item based on the current URL.
   * @param url - The URL after redirects from a NavigationEnd event.
   */
  private updateActiveItemFromUrl(url: string): void {
    const match: DeightonNavigationItem | undefined = findLeafByRoute(this.navigationMenuItems(), url);
    if (match) {
      this.activeNavItem.set(match.label);
    }
  }
}
